import { useState, useEffect, useMemo } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import SearchSelect from '../components/SearchSelect';
import { Wrench, Plus, Loader2, CheckCircle, Trash2, Clock, MapPin, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

export default function Interventies() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('open');
  const [customers, setCustomers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [products, setProducts] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [invoicing, setInvoicing] = useState(null);
  const EMPTY = { customer_id: '', property_id: '', hours: '', problem: '', accounting_notes: '', products: [], date: new Date().toISOString().slice(0, 10) };
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    try {
      const res = await api.get('/interventies');
      setItems(res.data || []);
    } catch { toast.error('Interventies laden mislukt'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    api.get('/customers').then(r => setCustomers(Array.isArray(r.data) ? r.data : r.data.customers || [])).catch(() => {});
    api.get('/properties').then(r => setProperties(Array.isArray(r.data) ? r.data : r.data.properties || [])).catch(() => {});
    api.get('/installation-products').then(r => setProducts(Array.isArray(r.data) ? r.data : r.data.products || [])).catch(() => {});
  }, []);

  const productOptions = useMemo(() => products.map(p => ({
    value: p.id,
    label: `${p.name}${p.brand ? ` (${p.brand})` : ''}`,
  })), [products]);

  const addProduct = (productId) => {
    const p = products.find(x => x.id === productId);
    if (!p) return;
    setForm(prev => prev.products.some(x => x.product_id === productId)
      ? prev
      : { ...prev, products: [...prev.products, { product_id: p.id, name: p.name, quantity: 1, unit_price: p.unit_price || 0 }] });
  };

  const customerOptions = useMemo(() => customers.map(c => ({
    value: c.id,
    label: c.company_name ? `${c.company_name} — ${c.first_name} ${c.last_name}` : `${c.first_name} ${c.last_name}`,
  })), [customers]);

  const propertyOptions = useMemo(() => {
    if (!form.customer_id) return [];
    return properties
      .filter(p => p.customer_id === form.customer_id)
      .map(p => ({ value: p.id, label: `${p.street} ${p.number}, ${p.postal_code} ${p.city}` }));
  }, [form.customer_id, properties]);

  const filtered = items.filter(i => filter === 'alle' || i.status === filter);
  const openCount = items.filter(i => i.status === 'open').length;
  const openHours = items.filter(i => i.status === 'open').reduce((s, i) => s + (i.hours || 0), 0);

  const save = async () => {
    if (!form.customer_id) { toast.error('Kies een klant'); return; }
    if (!form.hours) { toast.error('Vul het aantal uren in'); return; }
    setSaving(true);
    try {
      await api.post('/interventies', form);
      toast.success('Interventie geregistreerd');
      setDialogOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Opslaan mislukt'); }
    finally { setSaving(false); }
  };

  const createFactuur = async (it) => {
    setInvoicing(it.id);
    try {
      const res = await api.post(`/interventies/${it.id}/factuur`);
      toast.success(`Conceptfactuur ${res.data.factuur_number} aangemaakt — pas prijzen aan bij Facturen`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Factuur aanmaken mislukt'); }
    finally { setInvoicing(null); }
  };

  const markInvoiced = async (it) => {
    try {
      await api.put(`/interventies/${it.id}`, { status: 'gefactureerd' });
      toast.success('Gemarkeerd als gefactureerd');
      load();
    } catch { toast.error('Bijwerken mislukt'); }
  };

  const remove = async (it) => {
    try {
      await api.delete(`/interventies/${it.id}`);
      toast.success('Interventie verwijderd');
      load();
    } catch { toast.error('Verwijderen mislukt'); }
  };

  return (
    <div className="space-y-6" data-testid="interventies-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-orange-500/15 text-orange-600"><Wrench className="w-6 h-6" /></div>
          <div>
            <h1 className="text-2xl font-bold">Interventies</h1>
            <p className="text-sm text-muted-foreground">{openCount} aan te rekenen · {openHours.toFixed(1).replace('.', ',')} uur open</p>
          </div>
        </div>
        <div className="flex gap-2">
          {[['open', `Aan te rekenen (${openCount})`], ['gefactureerd', 'Gefactureerd'], ['alle', 'Alle']].map(([v, l]) => (
            <Button key={v} variant={filter === v ? 'default' : 'outline'} size="sm" onClick={() => setFilter(v)} data-testid={`interventie-filter-${v}`}>{l}</Button>
          ))}
          <Button onClick={() => { setForm(EMPTY); setDialogOpen(true); }} data-testid="nieuwe-interventie-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Nieuwe interventie
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">Geen interventies in deze lijst</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {filtered.map(it => (
            <Card key={it.id} data-testid={`interventie-card-${it.id}`}>
              <CardContent className="p-4 flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold">{it.customer_name}</p>
                    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${it.status === 'open' ? 'bg-amber-500/15 text-amber-600 border-amber-500/40' : 'bg-green-500/15 text-green-600 border-green-500/40'}`}>
                      {it.status === 'open' ? 'Aan te rekenen' : 'Gefactureerd'}
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Clock className="w-3 h-3" />{String(it.hours).replace('.', ',')} uur</span>
                  </div>
                  {it.problem && <p className="text-sm mt-1 whitespace-pre-wrap">{it.problem}</p>}
                  {(it.products || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {it.products.map((p, i) => (
                        <span key={i} className="inline-flex items-center rounded-full border border-border bg-muted/50 px-2 py-0.5 text-[11px]">
                          {p.quantity}× {p.name}
                        </span>
                      ))}
                    </div>
                  )}
                  {it.accounting_notes && <p className="text-xs mt-1.5 text-amber-600 dark:text-amber-400">Boekhouding: {it.accounting_notes}</p>}
                  {it.address && <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" />{it.address}</p>}
                  <p className="text-xs text-muted-foreground mt-1">
                    {it.date ? new Date(it.date + 'T00:00:00').toLocaleDateString('nl-BE') : ''} · door {it.created_by_name}
                    {it.status === 'gefactureerd' && it.factuur_ref ? ` · factuur ${it.factuur_ref}` : ''}
                    {it.status === 'gefactureerd' && it.invoiced_by_name ? ` · gefactureerd door ${it.invoiced_by_name}` : ''}
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  {it.status === 'open' && isAdmin && (
                    <Button size="sm" onClick={() => createFactuur(it)} disabled={invoicing === it.id} data-testid={`interventie-factuur-aanmaken-btn-${it.id}`}>
                      {invoicing === it.id ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FileText className="w-3.5 h-3.5 mr-1.5" />} Factuur aanmaken
                    </Button>
                  )}
                  {it.status === 'open' && (
                    <Button size="sm" variant="outline" className="border-green-500/50 text-green-600 hover:bg-green-500/10" onClick={() => markInvoiced(it)} data-testid={`interventie-factureer-btn-${it.id}`}>
                      <CheckCircle className="w-3.5 h-3.5 mr-1.5" /> Gefactureerd
                    </Button>
                  )}
                  {isAdmin && (
                    <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-600" onClick={() => remove(it)} data-testid={`interventie-delete-btn-${it.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="interventie-dialog">
          <DialogHeader>
            <DialogTitle>Nieuwe interventie</DialogTitle>
            <DialogDescription>Registreer een interventie zodat ze later aangerekend kan worden</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Klant *</Label>
              <div data-testid="interventie-klant-select">
                <SearchSelect
                  options={customerOptions}
                  value={form.customer_id}
                  onChange={(value) => setForm(p => ({ ...p, customer_id: value, property_id: '' }))}
                  placeholder="Selecteer klant..."
                  displayKey="label"
                  valueKey="value"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Adres</Label>
              <div data-testid="interventie-adres-select">
                <SearchSelect
                  options={propertyOptions}
                  value={form.property_id}
                  onChange={(value) => setForm(p => ({ ...p, property_id: value }))}
                  placeholder={form.customer_id ? 'Selecteer adres...' : 'Kies eerst een klant'}
                  displayKey="label"
                  valueKey="value"
                  disabled={!form.customer_id}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Aantal uren *</Label>
                <Input type="number" step="0.25" min="0" placeholder="bv. 1,5" value={form.hours} onChange={(e) => setForm(p => ({ ...p, hours: e.target.value }))} data-testid="interventie-uren-input" />
              </div>
              <div className="space-y-2">
                <Label>Datum</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm(p => ({ ...p, date: e.target.value }))} data-testid="interventie-datum-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Probleem / uitgevoerde werken</Label>
              <Textarea rows={3} placeholder="Beschrijf het probleem en wat er gedaan is..." value={form.problem} onChange={(e) => setForm(p => ({ ...p, problem: e.target.value }))} data-testid="interventie-probleem-input" />
            </div>
            <div className="space-y-2">
              <Label>Gebruikte producten</Label>
              <div data-testid="interventie-product-select">
                <SearchSelect
                  options={productOptions}
                  value=""
                  onChange={addProduct}
                  placeholder="Product toevoegen uit lijst..."
                  displayKey="label"
                  valueKey="value"
                />
              </div>
              {form.products.length > 0 && (
                <div className="space-y-1.5">
                  {form.products.map((p, i) => (
                    <div key={p.product_id} className="flex items-center gap-2 rounded-md border px-2 py-1.5 text-sm" data-testid={`interventie-product-rij-${i}`}>
                      <span className="flex-1 truncate">{p.name}</span>
                      <Input type="number" min="1" step="1" className="w-16 h-7 text-center" value={p.quantity}
                        onChange={(e) => setForm(prev => ({ ...prev, products: prev.products.map(x => x.product_id === p.product_id ? { ...x, quantity: e.target.value } : x) }))}
                        data-testid={`interventie-product-aantal-${i}`} />
                      <button className="text-muted-foreground hover:text-red-500" onClick={() => setForm(prev => ({ ...prev, products: prev.products.filter(x => x.product_id !== p.product_id) }))} data-testid={`interventie-product-verwijder-${i}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Opmerkingen voor de boekhouding</Label>
              <Textarea rows={2} placeholder="bv. dringend aanrekenen, speciaal tarief, ..." value={form.accounting_notes} onChange={(e) => setForm(p => ({ ...p, accounting_notes: e.target.value }))} data-testid="interventie-boekhouding-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuleren</Button>
            <Button onClick={save} disabled={saving} data-testid="interventie-opslaan-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wrench className="w-4 h-4 mr-2" />} Opslaan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
